// =============================================================================
// PER-SITE CONFIG — the single source of truth for everything that differs
// between sites. This file is the whole point of Phase 1: the three "photocopy"
// apps become ONE app where the difference between Trafford, Arndale, the
// kitchen and Meadowhall is just the row you read out of here.
//
// Adding or repointing a site happens HERE and nowhere else.
//
// The active site is chosen at RUNTIME from window.__SITE_KEY, which each
// deployment's index.html sets to one of the keys below (that single line is
// the only thing that differs between the per-site index.html files). This
// mirrors how BUILD_VERSION is already pinned in index.html, so it's the same
// pattern the apps use today — nothing new to learn. Falls back to
// DEFAULT_SITE_KEY for local dev.
// =============================================================================

// Shared Manchester Apps Script backend. Identical for every site today; a site
// can override it with its own `backend` field if one ever diverges (Meadowhall
// already has a SEPARATE forecast endpoint — see `forecastBackend` below).
export const BACKEND =
  "https://script.google.com/macros/s/AKfycbxnG2iQghhA837B0bZBsGxl_IiZXwc_ln3WDJx51yFeehMQ5gjkha4rtIcMva-oZRdfPg/exec";

// Meadowhall has its OWN forecast (Adam's Sheffield container-bound script),
// separate from the shared Manchester backend. Only Meadowhall reads it; every
// other action (stock, sell-out, EOD) still goes to BACKEND above.
export const MEADOWHALL_FORECAST_BACKEND =
  "https://script.google.com/macros/s/AKfycbxBzM5kf-LVcwczQQgfaCGpu4nUB9MfI4BIU9_flpyBIv8kCayb5qMXdOjngPCaMTmXWw/exec";

// Google-reviews leaderboard scope per site. The Manchester tablets (Trafford +
// Arndale) share ONE combined "manchester" board (staff cross-cover between the
// two); Meadowhall is a single-site board with no site toggle. `banner` = the
// home banner's scope, `tileDefault`/`allowed`/`region` drive the leaderboard
// tile. Kept identical to what the Manchester tills already show so folding
// Meadowhall in changes nothing for TC/Arndale.
// Arndale dropped 2026-08-17 (site closed 16/08/2026), leaving ONE scope.
// `allowed` had three entries — trafford / arndale / manchester — which is what
// still rendered an Arndale option in the leaderboard toggle on the Trafford
// till. It is now a single entry, and ReviewsTile only shows the toggle when
// allowedSites.length > 1 (foh.jsx), so the picker disappears rather than
// offering a pointless Trafford-vs-Manchester choice over identical data.
//
// The key stays "manchester" rather than becoming "trafford": it is what
// tileDefault already pointed at, so this is exactly the board staff see today,
// and REIGN_manchester / MCONFIG_manchester and every region-scoped cache are
// keyed on it. The backend now resolves that region to ['trafford'] alone
// (GoogleReviews._sitesInRegion_), so the data behind it is Trafford's.
const MANCHESTER_REVIEWS = {
  banner: "manchester", bannerName: "Manchester",
  tileDefault: "manchester", allowed: ["manchester"], region: "manchester",
};

// FOH roll list (used by Sell-Out Times + End-of-Day). The kitchen is BOH and
// uses its own ingredient list instead, so this lives on FOH sites only.
// This is the SHARED list; a shop that sells a different roll in one slot says
// so with `skuSwaps` on its SITES entry, and the app reads `site.skuList`.
export const SKU_LIST = [
  "Crispy", "Teriyaki", "Shrimp", "Bondi", "Cucumber", "Eggplant", "Salmon",
  "Cooked", "Spicy Tuna", "Chicken Avo", "Tuna Avo", "Hoisin Duck",
  "Katsu Prawn", "Teriyaki Beef",
];

// One shop's roll list: the shared list with that shop's swaps applied in
// place, so the swapped roll keeps the old one's row position. Labels are used
// as object keys (waste[sku], sellOutTimes[sku]) and the Quantities-sheet sync
// matches them against column A by exact string, so a swap label must be
// byte-identical to that shop's sheet row and distinct from every other label.
export function skuListFor(site) {
  const swaps = (site && site.skuSwaps) || {};
  return SKU_LIST.map(s => swaps[s] || s);
}

// Every site, every difference. Values verified against each app's own config
// block + CLAUDE.md (21 Jun 2026). `role` drives the FOH/BOH split in the app.
export const SITES = {
  tc: {
    key: "tc",
    id: "a1000000-0000-4000-8000-000000000001",
    name: "Trafford Centre",
    short: "TC",
    role: "FOH",
    clockInSiteKey: "tc",
    squareLocationId: "LFP88395DNDNV",
    quantitiesSheetId: "1OWuytHSyMtRmD79y078wpoVU4WrU9CdFleJg9i__Ylk",
    domain: "trafford.eatrolled.com",
    legacyNetlify: "rolled-trafford.netlify.app",
    reviews: MANCHESTER_REVIEWS,
    // FULL-SERVICE since the 24 Sep 2026 reopen: the Manchester kitchen closed on
    // 13 Sep 2026 and moved into this unit, so Trafford now runs the Meadowhall
    // shape — one app, one grid, every tablet identical, the per-action PIN is
    // the only identity. `hasKitchen` shows the kitchen tiles (Production
    // Forecast, Ingredient Wastage; Inbound Deliveries + the full compliance
    // module follow in week 1). `selfReceivesStock` switches Stock Requests to
    // the Meadowhall Request + Collect model: anyone requests, anyone collects
    // and marks Running Low / Out of Stock from the storeroom on site.
    hasKitchen: true,
    selfReceivesStock: true,
    // Sweet Chilli Chicken replaced Katsu Prawn at Trafford ONLY (JJ, 30 Sep
    // 2026) — Meadowhall keeps Katsu Prawn. "SC Chicken" is the row name on
    // the TC Quantities sheet (week 28.09 onwards).
    // Katsu Pumpkin replaced Tempura Eggplant at Trafford ONLY (JJ, 10 Oct
    // 2026) — Meadowhall keeps Eggplant. "Katsu Pumpkin" is the row name on
    // the TC Quantities sheet (week 05.10 onwards).
    skuSwaps: { "Eggplant": "Katsu Pumpkin", "Katsu Prawn": "SC Chicken" },
    // Which compliance module the tablet mounts. "full" = the kitchen's set
    // (HACCP day, probe calibration, fish freezing, emergency fridge, hygiene
    // declaration + cleaning / pest / corrective + the SOP / FSMS / supplier /
    // legal hub — src/lib/complianceFull.jsx); "subset" = the FOH-only cleaning /
    // pest / corrective screens in foh.jsx. Absent = the tile stays Coming Soon.
    // Storage units + probes for the HACCP screens are authored per site in the
    // Command Centre (Compliance → Emergency fridge procedure / Probe
    // Calibration); until Trafford's are entered the screens show an empty list
    // with a pointer there, never the closed kitchen's F1–F5 / P1–P3.
    // HELD (JJ, 20 Sep 2026, before the Wednesday 23 Sep reopen): "hold Trafford's
    // compliance tile too" — with the flag absent the tile is Coming Soon and the
    // full module is never mounted. Restore the line below (one-line PR + promote
    // after close) when Adam's compliance list (Notion scope page, section 15) is
    // done and JJ says go.
    // compliance: "full",
  },

  // CLOSED PERMANENTLY 16 Aug 2026. The entry is deliberately KEPT, not deleted:
  // getActiveSiteKey() falls through to DEFAULT_SITE_KEY ("tc") for an unknown
  // host, so deleting this would make a tablet still on arndale.eatrolled.com
  // silently write its rows as TRAFFORD. `closed` makes main.jsx hard-stop instead.
  // Historical IDs below stay accurate so archived Arndale rows remain readable.
  arndale: {
    key: "arndale",
    closed: true,
    closedOn: "2026-08-16",
    // What the closure screen calls this site. Separate from `name` because the
    // screen reads "<label> has closed" and staff know it as Rolled Arndale, not
    // "Manchester Arndale" (the name used in reports and historical rows).
    closedLabel: "Rolled Arndale",
    id: "a1000000-0000-4000-8000-000000000002",
    name: "Manchester Arndale",
    short: "ARN",
    role: "FOH",
    clockInSiteKey: "arndale",
    squareLocationId: "L4F9AZ06MNWCJ",
    quantitiesSheetId: "1th1LI-LtP-rck2HOcPXqD_HIolohXEbHPTLPv7cJuww",
    domain: "arndale.eatrolled.com",
    legacyNetlify: "rolled-arndale.netlify.app",
    reviews: MANCHESTER_REVIEWS,
  },

  // Manchester Kitchen — back of house (the central dark kitchen that supplied
  // Trafford + Arndale). Its SITE.id is the string "kitchen" (NOT a UUID) — kept
  // exactly as the live app has it (migrate as-is). No till of its own.
  //
  // CLOSED PERMANENTLY — last trading day 13 Sep 2026. The kitchen moved into the
  // Trafford Centre unit for the 24 Sep 2026 reopen and Trafford (`tc`) is now the
  // full-service site: Production Forecast, Inbound Deliveries, Ingredient
  // Wastage and stock all live on trafford.eatrolled.com. Closed after the clean
  // week on the Trafford host (28 Sep 2026), exactly as Arndale was: the entry is
  // KEPT, not deleted — getActiveSiteKey() falls through to DEFAULT_SITE_KEY
  // ("tc") for an unknown host, so deleting it would make a tablet still on
  // mcr-kitchen.eatrolled.com silently mount the Trafford app under the kitchen's
  // origin. `closed` makes main.jsx hard-stop on the closure screen instead.
  // Historical IDs stay accurate so archived kitchen rows remain readable.
  // mcr-kitchen is left in the host sets (DUAL_RUN_HOSTS etc.) until the
  // decommission PR — harmless, but do NOT wait on it to "drain" anything: the
  // closure screen has no PIN session, so a queued Supabase row cannot land from
  // it. Nothing is lost either way — the Sheet was the source of truth and the
  // self-heal sweeps copy it into Supabase.
  mcr: {
    key: "mcr",
    closed: true,
    closedOn: "2026-09-13",
    closedLabel: "Manchester Kitchen",
    // Where the work went. Arndale has none (its staff moved on); the kitchen
    // team is still on site at Trafford, so the closure screen also shows a
    // "use the Trafford app" line and a link. A link the person taps, never an
    // automatic redirect.
    movedTo: { label: "Trafford", url: "https://trafford.eatrolled.com/" },
    id: "kitchen",
    name: "Manchester Kitchen",
    short: "MCR",
    role: "BOH",
    clockInSiteKey: "kitchen",
    domain: "mcr-kitchen.eatrolled.com",
    legacyNetlify: "rolled-mcr-kitchen.netlify.app",
    // The FOH sites this kitchen produced for (cross-site forecast view). Was
    // ["tc", "arndale"] until Arndale closed 16 Aug 2026, then ["tc"]. The app
    // never reads it (the kitchen fan-outs have their own lists in
    // boh-kitchen.jsx); tools/lane_ae_active_sites.mjs derives KITCHEN_SERVED
    // from it, which is now empty — right, because a closed kitchen supplies no one.
    servesSites: [],
  },

  // Meadowhall (Sheffield) — folded into the unified app in Phase 3 (6 Jul).
  // Same FOH app as TC/Arndale (identical roll list, same shared BACKEND for
  // stock/sell-out/EOD) with two Sheffield specifics carried as config: its own
  // `forecastBackend` (a separate Sheffield forecast script) and a single-site
  // `reviews` scope (no combined "manchester" board). `id` is cosmetic — the DB
  // resolves the site by `key`, so a `sites` row keyed 'meadowhall' must exist.
  meadowhall: {
    key: "meadowhall",
    id: "a1000000-0000-4000-8000-000000000003",
    name: "Meadowhall",
    short: "MDH",
    role: "FOH",
    clockInSiteKey: "meadowhall",
    squareLocationId: "L12V8AC3MTZYP",
    domain: "meadowhall.eatrolled.com",
    legacyNetlify: "rolled-meadowhall.netlify.app",
    forecastBackend: MEADOWHALL_FORECAST_BACKEND,
    // Standalone site: no back-of-house kitchen supplies it, so nothing ever
    // fires the kitchen `stockDelivered` event that closes a stock request on
    // the Manchester sites. Meadowhall's own floor collects from its store room,
    // so the FOH Stock Requests tile must show a "mark collected" control (see
    // StockRequests in foh.jsx). Deliberately ABSENT on kitchen-served sites
    // (tc/arndale) so they keep the delivered=closed model with no redundant /
    // racing FOH close path. Restores the collect flow the old standalone
    // Meadowhall app had, which the unified merge (a7e4dda) dropped.
    selfReceivesStock: true,
    // Full-service: the kitchen is on the premises. Drives the kitchen tiles
    // (today: Ingredient Wastage) instead of a hardcoded site key per tile.
    hasKitchen: true,
    // FOH compliance subset (cleaning · pest · corrective) — Meadowhall's live
    // statutory writer (339 CleaningLog rows on the Sheffield app). On the
    // unified app the seed tile is on:false and no tile_overrides row ever
    // switched it on, so it has shown "Soon" here since the cut-over — the 7 Jul
    // 2026 logging gap the weekly health check keeps reporting. "subset" makes
    // the tile live again without touching the screens themselves.
    // HELD (JJ, 20 Sep 2026, before the first Meadowhall promotion of the week-1
    // build): "don't make the compliance live on the tablets yet". With the flag
    // absent the tile stays Coming Soon on Meadowhall exactly as it is today.
    // Restore the line below (one-line PR + promote) when Adam's compliance list
    // (Notion scope page, section 15) is done and JJ says go.
    // compliance: "subset",
    reviews: {
      banner: "meadowhall", bannerName: "Meadowhall",
      tileDefault: "meadowhall", allowed: ["meadowhall"], region: "meadowhall",
    },
  },
};

// Clock-In is currently OFF everywhere: Bizimply is the source of truth for
// payroll and per-action PINs already prove presence (decision 30 Apr 2026).
// Kept as one global so a future re-enable is a one-line change. Migrating the
// live behaviour exactly: this stays false in Phase 1.
export const CLOCK_IN_ENABLED = false;

export const DEFAULT_SITE_KEY = "tc";

// hostname → site-key lookup, built from each site's `domain`.
const DOMAIN_TO_KEY = Object.values(SITES).reduce((map, s) => {
  if (s.domain) map[s.domain.toLowerCase()] = s.key;
  return map;
}, {});

// Which site is this deployment serving? Resolved in priority order:
//   1. ?site=tc|arndale|mcr|meadowhall — explicit override for staging/testing
//      (lets us preview any site on the one staging link).
//   2. the domain the kiosk is on — production: trafford.eatrolled.com → tc,
//      mcr-kitchen.eatrolled.com → mcr, etc. ONE deployment serves every site.
//   3. window.__SITE_KEY from index.html — per-deployment default (staging = tc).
//   4. DEFAULT_SITE_KEY.
export function getActiveSiteKey() {
  if (typeof window !== "undefined") {
    try {
      const q = new URLSearchParams(window.location.search).get("site");
      if (q && SITES[q]) return q;
    } catch (e) {}
    const host = (window.location.hostname || "").toLowerCase();
    if (DOMAIN_TO_KEY[host]) return DOMAIN_TO_KEY[host];
    if (window.__SITE_KEY && SITES[window.__SITE_KEY]) return window.__SITE_KEY;
  }
  return DEFAULT_SITE_KEY;
}

export function getSite(key) {
  const site = SITES[key];
  if (!site) return null;
  return {
    ...site,
    backend: site.backend || BACKEND,
    skuList: skuListFor(site),
    isBOH: site.role === "BOH",
    isFOH: site.role === "FOH",
  };
}

// The resolved config for whichever site this deployment is. This is what the
// app reads instead of the old hard-coded `const SITE = {...}` block.
export function getActiveSite() {
  return getSite(getActiveSiteKey());
}
